import { Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {catchError, distinctUntilChanged, first, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import { Observable, of, ReplaySubject } from 'rxjs';
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Note } from "../../../shared/interface/note";
import { Partner } from "../../../shared/interface/partner";
import { Ofwca } from "../../../shared/interface/ofwca";
import { NoteService } from "../../../shared/services/note.service";
import { FormHelperService } from "../../../shared/services/form-helper.service";
import { Uuid } from "../../../shared/interface/response/uuid";
import { HttpErrorResponse } from "@angular/common/http";
import { NoteAddDialogComponent } from "../note-add-dialog/note-add-dialog.component";
import { UserPermissions } from "../../../shared/interface/user-permissions";
import { FacilitiesVerificationSalesProcessAddComponent
} from "../../facilities/facilities-verification-sales-process-add/facilities-verification-sales-process-add.component";
import { PartnerFacility } from "../../../shared/interface/partner-facility";
import { FacilityService } from "../../../shared/services/facility.service";
import { ListResponse } from "../../../shared/interface/response/list-response";
import {
  FILE_TYPE_SALES_PROCESS_VERIFICATION_FORM,
  TYPE_OF_MEETING_MEETING,
  OFWCA_STATUS_INACTIVE, TYPE_OF_MEETING,
} from "../../../shared/services/dict.service";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {
  PartnerFacilityVerificationSalesProcess
} from "../../../shared/interface/partner-facility-verification-sales-process";
import {DocumentAddDialogComponent} from "../../documents/document-add-dialog/document-add-dialog.component";
import {Profile} from "../../../shared/interface/profile";
import {AuthService} from "../../../shared/services/auth.service";
import {PartnerService} from "../../../shared/services/partner.service";
import {DictService} from "../../../shared/services/dict.service";
import {OfwcaService} from "../../../shared/services/ofwca.service";

@Component({
  selector: 'app-note-add',
  templateUrl: './note-add.component.html',
  styleUrls: ['./note-add.component.scss'],
})
export class NoteAddComponent implements OnInit, OnDestroy {
  @Input() noteObject?: Note;
  @Input() note: Observable<Note>;
  @Input() partnerObject?: Partner;
  @Input() ofwcaObject?: Ofwca;
  @Output() noteCreated = new EventEmitter<string>();
  @Output() closeModification = new EventEmitter<void>();
  @Input() dialogRef?: MatDialogRef<NoteAddDialogComponent>;
  @Input() control?: FormControl;

  facility: ReplaySubject<PartnerFacility> = new ReplaySubject(1);
  facilityObject?: PartnerFacility = null;
  noteForm: FormGroup;
  userPermissions: ReplaySubject<UserPermissions>;
  ofwca: Ofwca[] = [];
  ofwcas: ReplaySubject<Ofwca[]> = new ReplaySubject<Ofwca[]>(1);
  formErrors: Record<string, any> = {};

  meetingDateControl: FormControl;
  meetingTimeControl: FormControl;
  meetingEndDateControl: FormControl;
  ofwcaControl: FormControl;
  ofwcasControl: FormControl;
  ofwcaSelect: FormControl;
  partnerControl: FormControl;
  partnerSelect: FormControl;
  partnerOutsideStructureControl: FormControl;
  partnerOutsideStructureSelect: FormControl;
  noteData: FormControl;
  verificationSalesProcessControl: FormControl;
  resultsControl: FormControl;
  trainingControl: FormControl;
  relationsControl: FormControl;
  controlControl: FormControl;
  other: FormControl;
  otherControl: FormControl;
  recruitmentControl: FormControl;
  facilityControl: FormControl;
  typeOfMeetingControl: FormControl;
  partnerOfwcas: Ofwca[] = [];
  tookPlaceControl: FormControl;

  ofwcasLoading = false;
  processingRequest = false;
  processingVerificationRequest = false;
  verificationSales: string;
  partnerFacilityVerificationSalesProcessObject: PartnerFacilityVerificationSalesProcess;
  partnerFacilityVerificationSalesProcess: ReplaySubject<PartnerFacilityVerificationSalesProcess>
    = new ReplaySubject<PartnerFacilityVerificationSalesProcess>(1);
  profile: Profile;
  private meetingTypeId: number;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private facilityService: FacilityService,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private noteService: NoteService,
    private partnerService: PartnerService,
    private dialog: MatDialog,
    private dictService: DictService,
  ) {
    this.noteForm = this.fb.group({
      typeOfMeeting: this.typeOfMeetingControl = fb.control('', [Validators.required]),
      meetingDateControl: this.meetingDateControl = fb.control('', [Validators.required]),
      meetingTimeControl: this.meetingTimeControl = fb.control('', [Validators.required]),
      meetingEndDateControl: this.meetingEndDateControl = fb.control(''),
      verificationSalesProcess: this.verificationSalesProcessControl = fb.control(false),
      results: this.resultsControl = fb.control(false),
      training: this.trainingControl = fb.control(false),
      relations: this.relationsControl = fb.control(false),
      control: this.controlControl = fb.control(false),
      other: this.other = fb.control(''),
      recruitment: this.recruitmentControl = fb.control(false),
      partner: this.partnerControl = fb.control('', [Validators.required]),
      ofwca: this.ofwcaControl = fb.control('', [Validators.required]),
      ofwcasControl: this.ofwcasControl = fb.control([]),
      partnerOutsideStructureControl: this.partnerOutsideStructureControl = fb.control('', [Validators.required]),
      partnerOutsideStructureSelect: this.partnerOutsideStructureSelect = fb.control(false),
      noteData: this.noteData = fb.control(''),
      partnerSelect: this.partnerSelect = fb.control(false),
      ofwcaSelect: this.ofwcaSelect = fb.control(false),
      facility: this.facilityControl = fb.control(''),
      otherControl: this.otherControl = fb.control(false),
      tookPlace: this.tookPlaceControl = fb.control(false),
    });
  }

  ngOnInit(): void {
    this.noteForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.noteForm.valid);
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
        this.dictService.getId(TYPE_OF_MEETING_MEETING, TYPE_OF_MEETING)
          .subscribe((dictId: number) => {
            this.meetingTypeId = dictId;
            if (!this.noteObject && !this.note) {
              this.typeOfMeetingControl.setValue(this.meetingTypeId);
            }
          });
      });

    if (this.note) {
      this.note
        .pipe(takeUntil(this.destroyed$))
        .subscribe((note: Note) => {
          this.noteObject = note;
          this.initialize(note);
        });
    } else {
      this.initialized.next(true);
      const currentDate = new Date();
      const meetingDate = currentDate.toISOString().split('T')[0];
      const meetingTime = `${currentDate.getHours()}:${currentDate.getMinutes()}`;

      this.meetingDateControl.setValue(meetingDate);
      this.meetingTimeControl.setValue(meetingTime);
    }

    if (this.ofwcaObject) {
      this.ofwcaSelect.setValue(true);
      this.ofwcaControl.setValue(this.ofwcaObject.uuid || this.ofwcaObject);
      this.ofwcaControl.disable();
      this.ofwcaSelect.disable();
      this.partnerOutsideStructureControl.disable();
      this.partnerControl.clearValidators();
    }

    if (this.partnerObject) {
      this.loadOfwcasForPartner(this.partnerObject.uuid).subscribe();
      this.partnerSelect.setValue(true);
      this.partnerControl.setValue(this.partnerObject.uuid || this.partnerObject);
      this.partnerControl.disable();
      this.partnerSelect.disable();
      this.partnerOutsideStructureControl.disable();
      this.verificationSalesProcessControl.enable();
      this.ofwcaControl.clearValidators();
    }
    if (this.noteObject?.verificationSalesProcess) {
      this.verificationSalesProcessControl.disable();
      this.partnerControl.disable();
      this.partnerSelect.disable();
    }

    this.partnerSelect.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((value) => {
        if (value) {
          this.partnerControl.setValidators([Validators.required]);
          this.partnerControl.enable();
          this.partnerOutsideStructureControl.clearValidators();
          this.partnerOutsideStructureControl.disable();
          this.ofwcaControl.clearValidators();
          this.ofwcaControl.disable();
          if (!this.ofwcaSelect.value) {
            this.ofwcasControl.disable();
          } else {
            this.ofwcasControl.enable();
          }
        } else {
          if (this.typeOfMeetingControl.value === this.meetingTypeId) {
            this.partnerControl.setValidators([Validators.required]);
            this.ofwcaControl.setValidators([Validators.required]);
            this.partnerOutsideStructureControl.setValidators([Validators.required]);
          } else {
            this.partnerControl.clearValidators();
            this.ofwcaControl.clearValidators();
            this.partnerOutsideStructureControl.clearValidators();
          }
          this.partnerControl.reset('');
          this.verificationSalesProcessControl.disable();
          this.verificationSalesProcessControl.reset(false);
          this.facilityControl.reset('');
          if (this.partnerOutsideStructureSelect.value) {
            this.partnerOutsideStructureControl.setValidators([Validators.required]);
            this.partnerOutsideStructureControl.enable();
          }
        }
        this.partnerControl.updateValueAndValidity();
        this.partnerOutsideStructureControl.updateValueAndValidity();
        this.ofwcaControl.updateValueAndValidity();
        this.ofwcasControl.updateValueAndValidity();
      });


    this.ofwcaSelect.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((value) => {
        if (value) {
          this.ofwcaControl.setValidators([Validators.required]);
          this.partnerSelect.disable({ emitEvent: false });
          this.partnerOutsideStructureControl.disable({ emitEvent: false });
          this.partnerControl.disable({ emitEvent: false });

          if (this.partnerSelect.value) {
            this.loadOfwcasForPartner(this.partnerControl.value).subscribe();
            this.ofwcaControl.clearValidators();
            this.ofwcaControl.disable();
            this.ofwcasControl.setValidators([Validators.required]);
            this.ofwcasControl.enable();
          } else {
            this.ofwcaControl.setValidators([Validators.required]);
            this.ofwcaControl.enable();
            this.ofwcasControl.clearValidators();
            this.ofwcasControl.disable();
          }
        } else {
          this.ofwcaControl.reset('');
          this.ofwcaControl.disable();
          this.ofwcasControl.clearValidators();
          this.ofwcasControl.disable();
          if (!this.partnerObject && !this.noteObject?.facility)
          {
            this.partnerControl.enable();
          }
        }
        this.ofwcaControl.updateValueAndValidity();
        this.ofwcasControl.updateValueAndValidity();
      });

    this.partnerControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        tap(() => {
          this.partnerOfwcas = [];
          if (!this.ofwcaObject){
            this.ofwcaSelect.reset();
            this.ofwcasControl.setValue([]);
          }
          if (this.typeOfMeetingControl.value === this.meetingTypeId) {
            this.partnerControl.setValidators([Validators.required]);
            this.partnerControl.updateValueAndValidity();
          }
          this.verificationSalesProcessControl.setValue(false);
          this.facilityControl.reset();
        }),
        switchMap(partnerId => {
          const id = typeof partnerId === 'object' ? partnerId.uuid : partnerId;
          return this.partnerService.getPartnerOfwcas(id)
            .pipe(catchError(() => of(null)));
        }),
      )
      .subscribe((response: ListResponse<Ofwca>) => {
        if (response && response.items) {
          this.partnerOfwcas = response.items.filter(ofwca => ofwca.status.key !== OFWCA_STATUS_INACTIVE);
        }
      });
    this.ofwcaControl.valueChanges
      .subscribe(() => {
        if (this.typeOfMeetingControl.value === this.meetingTypeId) {
          this.ofwcaControl.setValidators([Validators.required]);
        }
      });
    this.partnerOutsideStructureControl.valueChanges
      .subscribe(() => {
        if (this.typeOfMeetingControl.value === this.meetingTypeId) {
          this.partnerOutsideStructureControl.setValidators([Validators.required]);
        }
      });

    this.partnerOutsideStructureSelect.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        if (value) {
          this.verificationSalesProcessControl.disable();
          this.partnerOutsideStructureControl.enable({ emitEvent: false });
          this.partnerControl.disable({ emitEvent: false });
          this.ofwcaControl.disable({ emitEvent: false });
          this.ofwcasControl.disable({ emitEvent: false });
        } else {
          this.partnerOutsideStructureControl.reset();
          this.partnerOutsideStructureControl.disable({ emitEvent: false });
          this.partnerControl.enable({ emitEvent: false });

          if (this.ofwcaSelect.value) {
            if (this.partnerSelect.value) {
              this.ofwcasControl.enable({ emitEvent: false });
            } else {
              this.ofwcaControl.enable({ emitEvent: false });
            }
          }
        }
      });

    this.verificationSalesProcessControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$), distinctUntilChanged()
      )
      .subscribe((val: boolean) => {
        if (val)
        {
          this.facilityControl.setValidators([Validators.required]);
        }else{
          this.facilityControl.reset();
          this.facilityControl.clearValidators();
          this.facilityControl.updateValueAndValidity();
        }
      });

    this.otherControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        if (!value) {
          this.other.reset();
        }
      });

    this.facilityControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => {
        this.verificationSales = null;
        this.partnerFacilityVerificationSalesProcessObject = null;
      });

    this.typeOfMeetingControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (this.typeOfMeetingControl.value === this.meetingTypeId && !this.partnerControl.value && !this.ofwcaControl.value && !this.partnerOutsideStructureControl.value) {
          this.partnerControl.setValidators([Validators.required]);
          this.ofwcaControl.setValidators([Validators.required]);
          this.partnerOutsideStructureControl.setValidators([Validators.required]);
        } else {
          this.partnerControl.clearValidators();
          this.ofwcaControl.clearValidators();
          this.partnerOutsideStructureControl.clearValidators();
        }
        this.partnerControl.updateValueAndValidity({emitEvent: false});
        this.ofwcaControl.updateValueAndValidity({emitEvent: false});
        this.partnerOutsideStructureControl.updateValueAndValidity({emitEvent: false});

      });
  }

  initialize(note: Note): void {
    of(note)
      .pipe(
        tap(() => {
          const meetingDateTime = new Date(note.meetingDateTime);
          const meetingDate = meetingDateTime.toISOString().split('T')[0];
          const meetingTime = `${meetingDateTime.getUTCHours()}:${meetingDateTime.getUTCMinutes()}`;

          this.noteForm.reset({
            meetingDateControl: meetingDate,
            meetingTimeControl: meetingTime,
            typeOfMeeting: note.typeOfMeeting.id,
            meetingEndDateControl: note.meetingEndDate,
            facility: note.facility?.id,
            results: note.results,
            training: note.training,
            relations: note.relations,
            control: note.control,
            other: note.other,
            ofwcasControl: note.notePartnerOfwcas.map((ofwca: Ofwca) => ofwca.uuid),
            noteData: note.noteData,
            partner: note.partner?.uuid,
            ofwca: note.ofwca?.uuid,
            partnerOutsideStructureControl: note.partnerOutsideStructure,
            partnerSelect: !!note.partner,
            ofwcaSelect: !!note.ofwca || (note.notePartnerOfwcas && note.notePartnerOfwcas.length > 0),
            partnerOutsideStructureSelect: !!note.partnerOutsideStructure,
            recruitment: note.recruitment,
            tookPlace: note.tookPlace,
            otherControl: !!note.other,
            verificationSales: note.verificationSales,
          });
          this.ofwcaControl.clearValidators();
          this.partnerControl.clearValidators();
          this.partnerOutsideStructureControl.clearValidators();
          this.noteForm.updateValueAndValidity();
          if (this.noteObject.facility && this.noteObject.verificationSalesProcess) {
            this.facilityControl.setValue(note.facility);
            this.facilityControl.disable();
            this.partnerControl.disable();
            this.verificationSalesProcessControl.disable();
            this.verificationSalesProcessControl.setValue(true);
          }
          if (note.notePartnerOfwcas && note.notePartnerOfwcas.length > 0) {
            this.ofwcaSelect.setValue(true);

            this.loadOfwcasForPartner(note.partner.uuid)
              .pipe(
              tap(() => {
                this.ofwcasControl.enable();
                this.ofwcaControl.disable();
                this.ofwcasControl.updateValueAndValidity();
              }),
              first()
            ).subscribe();
          }
          this.noteForm.updateValueAndValidity();
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.initialized.next(true);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  addVerificationSalesProcess(): void {
    this.processingVerificationRequest = true;
    this.ofwca = [];
    this.loadOfwcas().pipe(
      first(),
      switchMap((ofwcas) => this.dialog.open(FacilitiesVerificationSalesProcessAddComponent, {
        width: '70%',
        data: { partnerFacility: this.facilityControl.value, ofwcas },
        height: '90%',
        maxHeight: '90%',
        panelClass: 'full-height-dialog',
      }).afterClosed()),
      tap((result: any) => {
        if (result?.success) {
          this.verificationSales = result.apiResponse?.id;
          this.loadVerificationSales();
        }
        this.processingVerificationRequest = false;
      })
    ).subscribe();
  }

  loadOfwcas(): Observable<Ofwca[]> {
    return this.facilityService.getFacilityOfwcas(this.facilityControl.value.address.uuid).pipe(
      tap((ofwcas: ListResponse<Ofwca>) => {
        const activeOfwcas = ofwcas.items.filter(ofwca => ofwca.status.key !== OFWCA_STATUS_INACTIVE);
        this.ofwcas.next(activeOfwcas);
        this.ofwca = activeOfwcas;
      }),
      map((ofwcas: ListResponse<Ofwca>) => ofwcas.items.filter(ofwca => ofwca.status.key !== OFWCA_STATUS_INACTIVE))
    );
  }

  downloadCertificates(id: any): void {
    this.facilityService.downloadVeridicationSales(this.partnerFacilityVerificationSalesProcessObject.id.toString())
        .subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
          });
        }, (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
  }

  addDocument(): void {
    const dialogRef = this.dialog.open(DocumentAddDialogComponent, {
      width: '55%',
      data: {
        facilitiesVerificationObject: this.partnerFacilityVerificationSalesProcessObject,
        types: [FILE_TYPE_SALES_PROCESS_VERIFICATION_FORM],
      },
    });
    dialogRef.afterClosed();
  }

  loadVerificationSales(): void {
    this.facilityService.getFacilityVerificationSalesProcess(this.verificationSales)
      .subscribe((partnerFacilityVerificationSalesProcess: PartnerFacilityVerificationSalesProcess) => {
        this.partnerFacilityVerificationSalesProcess.next(partnerFacilityVerificationSalesProcess);
        this.partnerFacilityVerificationSalesProcessObject = partnerFacilityVerificationSalesProcess;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Nie udało się pobrać danych placówki', error.status.toString(), {
          duration: 2000
        });
      });
  }

  loadOfwcasForPartner(partnerId: string): Observable<Ofwca[]> {
    this.ofwcasLoading = true;

    return this.partnerService.getPartnerOfwcas(partnerId).pipe(
      takeUntil(this.destroyed$),
      map((response: ListResponse<Ofwca>) =>
        response.items.filter(ofwca => ofwca.status.key !== OFWCA_STATUS_INACTIVE)
      ),
      tap((filteredOfwcas: Ofwca[]) => {
        this.partnerOfwcas = filteredOfwcas;
        this.ofwcasLoading = false;
      })
    );
  }


  cancel(): void {
    if (this.note) {
      this.closeModification.emit();
      return;
    }

    if (!this.dialogRef) {
      this.router.navigate(['/notatki']);
    } else {
      this.dialogRef.close();
    }
  }

  save(resetForm = false): void {
    this.processingRequest = true;
    if (!this.noteForm.valid) {
      this.noteForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 3000,
      });
      this.processingRequest = false;
      return;
    }
    this.formHelper.removeBackendErrorState(this.noteForm, this.formErrors);
    this.formErrors = {};

    const formData = this.noteForm.getRawValue();

    if (this.ofwcaSelect.value) {
      if (!this.partnerSelect.value && !this.ofwcaControl.value) {
        this.ofwcaSelect.setValue(false);
      } else if (this.partnerSelect.value &&
        (!this.ofwcasControl.value ||
          (Array.isArray(this.ofwcasControl.value) && this.ofwcasControl.value.length === 0))) {
        this.ofwcaSelect.setValue(false);
      }
    }
    if (this.meetingEndDateControl.value) {
      formData.meetingEndDate = this.meetingEndDateControl.value;
    }

    if (this.partnerOutsideStructureControl.value) {
      formData.regional = this.profile.regionalId;
      formData.partnerOutsideStructure = this.partnerOutsideStructureControl.value;
    }

    if (this.facilityControl.value && this.verificationSalesProcessControl.value) {
      formData.facility = this.facilityControl.value.id;
    }else if (this.facilityControl.value && !this.verificationSalesProcessControl.value) {
      formData.facility = this.facilityControl.value;
    }

    if (this.meetingDateControl && this.meetingTimeControl) {
      const meetingDate = this.meetingDateControl.value;
      const meetingTime = this.meetingTimeControl.value;
      formData.meetingDateTime = this.combineDateAndTime(meetingDate, meetingTime);
    }

    if (this.verificationSales) {
      formData.verificationSales = this.verificationSales;
    } else if (this.noteObject?.verificationSales) {
      formData.verificationSales = this.noteObject.verificationSales.id;
    }
    if (this.ofwcaSelect.value && !this.partnerSelect.value && this.ofwcaControl.value) {
      formData.ofwca = this.ofwcaControl.value;
      delete formData.notePartnerOfwcas;
    }
    else if (this.ofwcaSelect.value && this.partnerSelect.value && this.ofwcasControl.value) {
      const ofwcasValue = this.ofwcasControl.value;
      if (Array.isArray(ofwcasValue) && ofwcasValue.length > 0) {
        formData.notePartnerOfwcas = ofwcasValue.map(ofwca =>
          typeof ofwca === 'string' ? ofwca : ofwca.uuid
        ).filter(uuid => !!uuid);
      }
      delete formData.ofwca;
    } else {
      delete formData.ofwca;
      delete formData.notePartnerOfwcas;
    }

    this.formHelper.removeBackendErrorState(this.noteForm, this.formErrors);
    this.formErrors = {};

    const noteAction = this.noteObject
      ? this.noteService.updateNote(this.noteObject.uuid, formData)
      : this.noteService.addNote(formData);

    noteAction.pipe(first()).subscribe(
      (response: Uuid) => {
        this.snack.open(this.noteObject ? 'Pomyślnie zaktualizowano notatkę' : 'Pomyślnie utworzono notatkę', '', { duration: 2000 });
        this.processingRequest = false;

        if (resetForm) {
          this.noteForm.reset({});
          const currentDate = new Date();
          const meetingDate = currentDate.toISOString().split('T')[0];
          const meetingTime = `${currentDate.getHours()}:${currentDate.getMinutes()}`;
          this.typeOfMeetingControl.setValue(this.meetingTypeId);
          this.meetingDateControl.setValue(meetingDate);
          this.meetingTimeControl.setValue(meetingTime);
          return;
        }

        if (this.noteObject || this.ofwcaObject || this.partnerObject || this.dialogRef) {
          this.closeModification.emit();
          this.dialogRef?.close(true);
        } else {
          this.router.navigate(['/notatki/szczegoly', response.uuid]);
        }
      },
      (error: HttpErrorResponse) => {
        this.processingRequest = false;
        if (error.status === 400 && error.error?.error?.list) {
          this.formErrors = error.error.error.list;
          this.formErrors.typeOfMeetingControl &&
          this.meetingTimeControl.setErrors({ backendError: this.formErrors.typeOfMeetingControl[0] });
          this.formHelper.setBackendErrorState(this.noteForm, this.formErrors);
        } else {
          this.snack.open('Wystąpił błąd podczas zapisu', '', { duration: 2000 });
        }
      }
    );
  }

  private combineDateAndTime(date: Date, time: string): string {
    const [hours, minutes] = time.split(':').map(Number);
    const combinedDate = new Date(date);
    combinedDate.setHours(hours);
    combinedDate.setMinutes(minutes);

    const utcDate = new Date(
      Date.UTC(
        combinedDate.getFullYear(),
        combinedDate.getMonth(),
        combinedDate.getDate(),
        combinedDate.getHours(),
        combinedDate.getMinutes(),
      )
    );
    return utcDate.toISOString().slice(0, 16).replace('T', ' ');
  }
}
